import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
} from '@mui/material';
import { RecordVoiceOver, Info } from '@mui/icons-material';

/**
 * AI Permission Dialog Component
 * Shown only to Edifiers when they join the meeting. Asks for permission to record with AI note-taker.
 * Teachers do not see this dialog; the Edifier's choice applies to the meeting.
 */
function AIPermissionDialog({ open, onAllow, onDeny }) {
  return (
    <Dialog
      open={open}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
        },
      }}
    >
      <DialogTitle sx={{ textAlign: 'center', pt: 3 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 1,
            mb: 1,
          }}
        >
          <RecordVoiceOver sx={{ fontSize: 40 }} />
        </Box>
        <Typography variant="h5" fontWeight="bold">
          AI Note-Taker
        </Typography>
      </DialogTitle>

      <DialogContent sx={{ px: 3, pb: 2 }}>
        <Box
          sx={{
            backgroundColor: 'rgba(255, 255, 255, 0.1)',
            borderRadius: 2,
            p: 2,
            mb: 2,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 2 }}>
            <Info sx={{ fontSize: 20, mt: 0.3 }} />
            <Typography variant="body2" sx={{ lineHeight: 1.6 }}>
              Would you like to enable AI note-taking for this meeting?
            </Typography>
          </Box>

          <Typography variant="body2" sx={{ mb: 1, fontWeight: 'bold' }}>
            If you allow:
          </Typography>
          <Typography variant="body2" component="ul" sx={{ pl: 2, mb: 2 }}>
            <li>The meeting will be transcribed in real-time</li>
            <li>An AI summary will be generated after the meeting</li>
            <li>Both participants will receive the summary via email</li>
          </Typography>

          <Typography variant="body2" sx={{ mb: 1, fontWeight: 'bold' }}>
            If you deny:
          </Typography>
          <Typography variant="body2" component="ul" sx={{ pl: 2 }}>
            <li>Only a raw transcript will be recorded</li>
            <li>No AI summary will be generated</li>
            <li>No emails will be sent</li>
          </Typography>
        </Box>

        <Typography
          variant="caption"
          sx={{
            display: 'block',
            textAlign: 'center',
            color: 'rgba(255, 255, 255, 0.8)',
            fontStyle: 'italic',
          }}
        >
          This decision cannot be changed once made
        </Typography>
      </DialogContent>

      <DialogActions sx={{ justifyContent: 'center', pb: 3, gap: 2 }}>
        <Button
          onClick={onDeny}
          variant="outlined"
          sx={{
            color: 'white',
            borderColor: 'rgba(255, 255, 255, 0.5)',
            px: 3,
            '&:hover': {
              borderColor: 'white',
              backgroundColor: 'rgba(255, 255, 255, 0.1)',
            },
          }}
        >
          Deny
        </Button>
        <Button
          onClick={onAllow}
          variant="contained"
          sx={{
            backgroundColor: 'white',
            color: '#667eea',
            px: 3,
            fontWeight: 'bold',
            '&:hover': {
              backgroundColor: 'rgba(255, 255, 255, 0.9)',
            },
          }}
        >
          Allow AI Note-Taker
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default AIPermissionDialog;
